import React, { useCallback, useEffect, useMemo } from 'react';
import Animated from 'react-native-reanimated';
import { StyleSheet, View } from 'react-native';
import TouchableBounce from 'utils/packages/TouchableBounce';
import { rem, AppView, Button, CardView, IconArrow, SCROLL_INDICATOR_INSETS, withAnimatedNavigationHeader, } from 'design-system';
import StatusBar from 'design-system/StatusBar';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import useColors from 'hooks/useColors';
import { selectAddress } from 'actions';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import { selectPrimaryAddressForGB, selectSecondaryAddresses, } from 'reducers/selectors';
import { OnboardingFlow } from 'utils/types/navigation';
import { popToOrNavigate } from 'utils/navigationv6';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { setPendingPreviousAddress } from 'features/credit-score/reducers/creditScoreOnboarding';
import FormattedAddress from 'features/invest/components/FormattedAddress';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { useMarginBottom } from 'hooks/useMarginBottom';
const ChoosePreviousAddressScreen = withAnimatedNavigationHeader(({ navigation, scrollHandler, route }) => {
    const colors = useColors();
    const dispatch = useAppDispatch();
    const flow = route.params?.flow;
    const secondaryAddresses = useAppSelector(selectSecondaryAddresses);
    const store = useAppStore();
    useEffect(() => {
        navigation.setOptions({
            headerRight: () => (<HelpScreenButton articlesKey={flow === OnboardingFlow.CreditCard
                    ? HELP_ARTICLES_KEY.LOAN_ONBOARDING
                    : undefined}/>),
            headerTintColor: colors.elements.primary,
        });
    }, [colors.elements.primary, flow]);
    useEffect(() => {
        dispatch(selectAddress(undefined));
    }, []);
    const isCreditScoreFlow = flow === OnboardingFlow.CreditScore;
    const handlePress = useCallback((address) => () => {
        if (isCreditScoreFlow) {
            dispatch(setPendingPreviousAddress(address));
            const isReviewKYCAddressInStack = navigation
                .getState()
                .routes.some((r) => r.name === 'CreditScoreReviewKYCAddress');
            const primaryAddress = selectPrimaryAddressForGB(store.getState());
            if (isReviewKYCAddressInStack && primaryAddress) {
                navigation.navigate('AddressInputScreen', {
                    address,
                    isAddingSecondaryAddress: true,
                    flow: OnboardingFlow.CreditScore,
                });
                return;
            }
            popToOrNavigate(navigation, 'CreditScoreRegistration');
            return;
        }
        dispatch(selectAddress(address));
        navigation.goBack();
    }, [isCreditScoreFlow]);
    const handleAddNewAddress = useCallback(() => {
        navigation.navigateDeprecated('AddressPostCodeScreen', {
            isAddingSecondaryAddress: true,
            flow: flow === OnboardingFlow.CreditScore ? flow : undefined,
        });
    }, [flow]);
    const renderItem = useCallback(({ item: address }) => (<TouchableBounce onPress={handlePress(address)}>
            <CardView noMarginTop withPadding style={styles.row}>
              <View>
                <FormattedAddress {...address} uk/>
              </View>
              <IconArrow />
            </CardView>
          </TouchableBounce>), [handlePress]);
    const ListHeaderComponent = useMemo(() => (<ScreenInfoHeader smallSubTitle title="Choose previous address" subTitle="Providing your address history helps us to find your credit report"/>), []);
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING);
    return (<AppView>
          <StatusBar barStyle="dark-content" animated/>
          <Animated.FlatList renderItem={renderItem} onScroll={scrollHandler} scrollEventThrottle={16} data={secondaryAddresses} ListHeaderComponent={ListHeaderComponent} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={paddingBottom}/>
          <Button brand floating title="Add new address" onPress={handleAddNewAddress}/>
        </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
export default ChoosePreviousAddressScreen;
const styles = StyleSheet.create({
    row: {
        alignItems: 'center',
        flexDirection: 'row',
        gap: rem(16),
        justifyContent: 'space-between',
    },
});
